import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeColorSet {
  accent?: string;
  background?: string;
  foreground?: string;
}

export interface ThemeColors {
  light: ThemeColorSet;
  dark: ThemeColorSet;
}

interface ThemeProviderState {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  customColors: ThemeColors;
  setCustomColors: (colors: ThemeColors) => void;
}

const STORAGE_KEY = 'gift-ui-theme';
const COLORS_STORAGE_KEY = 'gift-ui-theme-colors';
const STYLE_ID = 'gift-custom-theme';

const emptyColors: ThemeColors = { light: {}, dark: {} };

function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) return `0 0% ${Math.round(l * 100)}%`;

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function luminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastForeground(hex: string): string {
  return luminance(hex) > 0.5 ? '0 0% 9%' : '0 0% 98%';
}

function buildCustomCss(colors: ThemeColors): string {
  const sections: string[] = [];

  const lightVars = buildVars(colors.light);
  if (lightVars) sections.push(`:root {\n${lightVars}\n}`);

  const darkVars = buildVars(colors.dark);
  if (darkVars) sections.push(`.dark {\n${darkVars}\n}`);

  return sections.join('\n');
}

function buildVars(set: ThemeColorSet): string {
  const lines: string[] = [];
  if (set.accent) {
    const hsl = hexToHsl(set.accent);
    lines.push(`  --primary: ${hsl};`);
    lines.push(`  --primary-foreground: ${contrastForeground(set.accent)};`);
    lines.push(`  --ring: ${hsl};`);
  }
  if (set.background) {
    const hsl = hexToHsl(set.background);
    lines.push(`  --background: ${hsl};`);
    lines.push(`  --card: ${hsl};`);
    lines.push(`  --popover: ${hsl};`);
    lines.push(`  --surface-0: ${hsl};`);
  }
  if (set.foreground) {
    const hsl = hexToHsl(set.foreground);
    lines.push(`  --foreground: ${hsl};`);
    lines.push(`  --card-foreground: ${hsl};`);
    lines.push(`  --popover-foreground: ${hsl};`);
  }
  return lines.join('\n');
}

function loadColors(): ThemeColors {
  try {
    const stored = localStorage.getItem(COLORS_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    /* ignore */
  }
  return { light: {}, dark: {} };
}

const ThemeContext = createContext<ThemeProviderState | undefined>(undefined);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
}) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(STORAGE_KEY) as Theme) || defaultTheme,
  );
  const [customColors, setCustomColorsState] =
    useState<ThemeColors>(loadColors);

  const resolvedTheme =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme;

  const setCustomColors = (colors: ThemeColors) => {
    setCustomColorsState(colors);
    const isEmpty =
      !colors.light.accent &&
      !colors.light.background &&
      !colors.light.foreground &&
      !colors.dark.accent &&
      !colors.dark.background &&
      !colors.dark.foreground;
    if (isEmpty) {
      localStorage.removeItem(COLORS_STORAGE_KEY);
    } else {
      localStorage.setItem(COLORS_STORAGE_KEY, JSON.stringify(colors));
    }
  };

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme, resolvedTheme]);

  // Inject custom color overrides
  useEffect(() => {
    let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    const css = buildCustomCss(customColors);
    if (css) {
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = STYLE_ID;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = css;
    } else if (styleEl) {
      styleEl.remove();
    }
  }, [customColors]);

  useEffect(() => {
    if (theme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setTheme('system');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{ theme, resolvedTheme, setTheme, customColors, setCustomColors }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}

export { emptyColors };
